import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  Receipt,
  Search,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { farmGet, STATO_FATTURA_LABELS } from "./shared";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "documenti", label: "Documenti contrattuali" },
  { id: "fatture", label: "Fatture farmacia" },
  { id: "extra", label: "Extra per ospite" },
  { id: "scadenze", label: "Scadenze" },
];

const DOC_VALIDITA = {
  valido: { label: "Valido", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  in_scadenza: { label: "In scadenza", className: "border-amber-200 bg-amber-50 text-amber-700" },
  scaduto: { label: "Scaduto", className: "border-rose-200 bg-rose-50 text-rose-700" },
  senza_scadenza: { label: "Valido", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  valido_fino_a_revoca: { label: "Valido", className: "border-sky-200 bg-sky-50 text-sky-700" },
  revocato: { label: "Revocato", className: "border-slate-300 bg-slate-50 text-slate-600" },
  mancante: { label: "Mancante", className: "border-slate-200 bg-slate-50 text-slate-500" },
};

const FIRMA_LABELS = {
  non_richiesta: "Non richiesta",
  da_firmare: "Da firmare",
  firmato: "Completata",
  da_verificare: "Da verificare",
};

const FATTURE_APERTE = new Set(["pubblicata", "visualizzata", "da_pagare", "bozza"]);

function monthISO() {
  return new Date().toISOString().slice(0, 7);
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
}

function formatEuro(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return `€ ${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const t = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
    const now = new Date();
    now.setHours(12, 0, 0, 0);
    return Math.round((t - now) / 86400000);
  } catch {
    return null;
  }
}

function docDisplayStatus(doc) {
  if (doc.mancante || !doc.id) {
    return DOC_VALIDITA.mancante;
  }
  if (doc.stato_firma === "da_firmare") {
    return { label: "Da firmare", className: "border-amber-200 bg-amber-50 text-amber-800" };
  }
  return DOC_VALIDITA[doc.stato_validita] || DOC_VALIDITA.senza_scadenza;
}

function KpiTile({ icon: Icon, label, value, linkLabel, onClick, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    rose: "bg-rose-50 text-rose-700",
    violet: "bg-violet-50 text-violet-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-2xl border border-slate-200/80 bg-white p-3.5 text-left shadow-sm transition hover:border-emerald-200 hover:shadow"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-slate-500">{label}</p>
          <p className="mt-0.5 text-xl font-semibold tracking-tight text-slate-900">{value}</p>
          {linkLabel ? (
            <p className="mt-1 text-[10px] font-medium text-emerald-700">{linkLabel}</p>
          ) : null}
        </div>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tones[tone] || tones.emerald}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
    </button>
  );
}

function DetailPanel({ kind, item, strutturaNome, onClose, onDownloadDoc, onDownloadFattura }) {
  if (!item) {
    return (
      <aside className="hidden w-[340px] shrink-0 xl:block">
        <div className="sticky top-0 rounded-2xl border border-dashed border-slate-200 bg-white/60 p-6 text-center">
          <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Elemento selezionato</p>
          <p className="mt-1 text-xs text-slate-400">Seleziona una riga dall&apos;elenco per il dettaglio.</p>
        </div>
      </aside>
    );
  }

  if (kind === "documento" || kind === "scadenza") {
    const st = docDisplayStatus(item);
    const scheda = item.organization_id
      ? `/farmacia/struttura/${encodeURIComponent(item.organization_id)}`
      : "/farmacia/strutture";
    return (
      <aside className="w-full shrink-0 xl:w-[340px]">
        <div className="sticky top-0 flex max-h-[calc(100vh-10rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-semibold text-slate-800">Documento selezionato</p>
            <button type="button" className="text-xs text-slate-400 hover:text-slate-600 xl:hidden" onClick={onClose}>
              Chiudi
            </button>
          </div>
          <div className="space-y-4 overflow-y-auto p-4 text-xs">
            <div className="flex items-start gap-2.5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <FileText className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900">{item.tipo_label || item.tipo}</p>
                <p className="mt-0.5 text-slate-500">{item.file_name || (item.mancante ? "Non caricato" : "—")}</p>
              </div>
            </div>
            <dl className="space-y-1.5">
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Struttura</dt>
                <dd className="text-right font-medium text-slate-800">{strutturaNome || "—"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Stato</dt>
                <dd>
                  <Badge variant="outline" className={`text-[10px] ${st.className}`}>
                    {st.label}
                  </Badge>
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Firma</dt>
                <dd className="font-medium text-slate-800">
                  {FIRMA_LABELS[item.stato_firma] || item.stato_firma || "—"}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Scadenza</dt>
                <dd className="font-medium text-slate-800">{formatWhen(item.scadenza)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Aggiornato</dt>
                <dd className="font-medium text-slate-800">{formatWhen(item.updated_at || item.created_at)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Tipo</dt>
                <dd className="font-medium text-slate-800">Contrattuale</dd>
              </div>
            </dl>
            <div className="flex flex-wrap gap-1">
              {item.mime_type?.includes("pdf") || (item.file_name || "").toLowerCase().endsWith(".pdf") ? (
                <Badge variant="outline" className="text-[10px]">
                  PDF
                </Badge>
              ) : null}
              {item.stato_firma === "firmato" ? (
                <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">
                  Firmato
                </Badge>
              ) : null}
              {item.classe_validita === "con_scadenza" ? (
                <Badge variant="outline" className="border-sky-200 bg-sky-50 text-[10px] text-sky-700">
                  Con scadenza
                </Badge>
              ) : null}
            </div>
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Azioni rapide
              </p>
              <div className="grid gap-1.5">
                <Button asChild variant="outline" size="sm" className="h-8 justify-start rounded-lg text-xs">
                  <Link to={scheda}>Carica / sostituisci in scheda</Link>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 justify-start rounded-lg text-xs"
                  disabled={!item.id}
                  onClick={() => onDownloadDoc(item)}
                >
                  Scarica PDF
                </Button>
              </div>
            </div>
          </div>
          <div className="mt-auto border-t border-slate-100 p-3">
            <Button asChild className="h-10 w-full rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">
              <Link to={scheda}>
                Apri scheda completa
                <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      </aside>
    );
  }

  if (kind === "fattura") {
    const st = STATO_FATTURA_LABELS[item.stato_fattura] || {
      label: item.stato_fattura,
      className: "bg-slate-50 text-slate-600 border-slate-200",
    };
    return (
      <aside className="w-full shrink-0 xl:w-[340px]">
        <div className="sticky top-0 flex max-h-[calc(100vh-10rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-semibold text-slate-800">Fattura selezionata</p>
            <button type="button" className="text-xs text-slate-400 hover:text-slate-600 xl:hidden" onClick={onClose}>
              Chiudi
            </button>
          </div>
          <div className="space-y-4 overflow-y-auto p-4 text-xs">
            <div className="flex items-start gap-2.5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-700">
                <Receipt className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900">
                  {item.numero_fattura ? `Fattura ${item.numero_fattura}` : item.file_name || "Fattura"}
                </p>
                <p className="mt-0.5 text-slate-500">{strutturaNome || "—"}</p>
              </div>
            </div>
            <dl className="space-y-1.5">
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Stato</dt>
                <dd>
                  <Badge variant="outline" className={`text-[10px] ${st.className}`}>
                    {st.label}
                  </Badge>
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Importo</dt>
                <dd className="font-medium text-slate-800">{formatEuro(item.importo)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Data</dt>
                <dd className="font-medium text-slate-800">{formatWhen(item.data_fattura)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Scadenza</dt>
                <dd className="font-medium text-slate-800">{formatWhen(item.scadenza)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Periodo</dt>
                <dd className="text-right font-medium text-slate-800">
                  {item.periodo_da || item.periodo_a
                    ? `${formatWhen(item.periodo_da)} – ${formatWhen(item.periodo_a)}`
                    : item.mese_riferimento || "—"}
                </dd>
              </div>
            </dl>
            {(item.riepilogo_extra || []).length > 0 ? (
              <p className="rounded-lg bg-violet-50 px-2.5 py-2 text-violet-800">
                Riepilogo extra: {(item.riepilogo_extra || []).length} ospiti
              </p>
            ) : null}
            <div className="grid gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 justify-start rounded-lg text-xs"
                onClick={() => onDownloadFattura(item)}
              >
                Scarica PDF
              </Button>
              <Button asChild variant="outline" size="sm" className="h-8 justify-start rounded-lg text-xs">
                <Link to={`/farmacia/fatturazione?org=${encodeURIComponent(item.organization_id || "")}`}>
                  Gestisci in Fatturazione
                </Link>
              </Button>
            </div>
          </div>
          <div className="mt-auto border-t border-slate-100 p-3">
            <Button asChild className="h-10 w-full rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">
              <Link to="/farmacia/fatturazione">
                Apri scheda completa
                <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      </aside>
    );
  }

  if (kind === "extra") {
    return (
      <aside className="w-full shrink-0 xl:w-[340px]">
        <div className="sticky top-0 flex max-h-[calc(100vh-10rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-semibold text-slate-800">Extra ospite</p>
            <button type="button" className="text-xs text-slate-400 hover:text-slate-600 xl:hidden" onClick={onClose}>
              Chiudi
            </button>
          </div>
          <div className="space-y-4 overflow-y-auto p-4 text-xs">
            <div>
              <p className="font-semibold text-slate-900">{item.ospite_nome || "Ospite"}</p>
              <p className="mt-0.5 text-slate-500">{strutturaNome}</p>
              {item.codice_fiscale ? (
                <p className="mt-1 font-mono text-[10px] text-slate-400">{item.codice_fiscale}</p>
              ) : null}
            </div>
            <p className="text-slate-600">
              Unità consegnate nel mese:{" "}
              <span className="font-semibold text-slate-900">{item.totale_unita ?? 0}</span>
            </p>
            <ul className="max-h-48 space-y-1 overflow-y-auto">
              {(item.righe || []).map((r, i) => (
                <li key={`${r.ordine_id}-${i}`} className="rounded-lg bg-slate-50 px-2 py-1.5 text-slate-700">
                  {r.prodotto_nome || r.prodotto_codice || "Prodotto"} × {r.quantita_consegnata}
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-slate-400">
              Riepilogo informativo da ordini consegnati (nessun importo automatico).
            </p>
          </div>
          <div className="mt-auto border-t border-slate-100 p-3">
            <Button asChild className="h-10 w-full rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">
              <Link to="/farmacia/ordini">Apri ordini</Link>
            </Button>
          </div>
        </div>
      </aside>
    );
  }

  return null;
}

export default function FarmaciaAmministrazione() {
  const { token } = useFarmaciaAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "documenti";

  const [strutture, setStrutture] = useState([]);
  const [documenti, setDocumenti] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [extraRows, setExtraRows] = useState([]);
  const [periodo, setPeriodo] = useState(monthISO());
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tipoFilter, setTipoFilter] = useState("all");
  const [statoFilter, setStatoFilter] = useState("all");
  const [orgFilter, setOrgFilter] = useState("all");
  const [selectedKey, setSelectedKey] = useState(null);

  const setTab = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const orgName = useCallback(
    (id) => {
      const s = strutture.find((x) => x.organization_id === id);
      return s?.denominazione || s?.nome || id || "—";
    },
    [strutture]
  );

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const strRes = await farmGet("/farmacia/strutture", token, { lite: true });
      const list = strRes.items || [];
      setStrutture(list);

      const [docLists, fatLists, extraLists] = await Promise.all([
        Promise.all(
          list.map((s) =>
            farmGet(
              `/farmacia/strutture/${encodeURIComponent(s.organization_id)}/documenti-essenziali`,
              token
            )
              .then((r) =>
                (r.items || []).map((d) => ({
                  ...d,
                  organization_id: d.organization_id || s.organization_id,
                  _key: `doc:${d.id || `${s.organization_id}-${d.tipo}`}`,
                }))
              )
              .catch(() => [])
          )
        ),
        Promise.all(
          list.map((s) =>
            farmGet("/farmacia/fatture", token, { organization_id: s.organization_id })
              .then((r) =>
                (r.items || []).map((f) => ({
                  ...f,
                  organization_id: f.organization_id || s.organization_id,
                  _key: `fat:${f.id}`,
                }))
              )
              .catch(() => [])
          )
        ),
        Promise.all(
          list.map((s) =>
            farmGet("/farmacia/fatture/riepilogo-extra", token, {
              organization_id: s.organization_id,
              periodo,
            })
              .then((r) =>
                (r.items || []).map((e, idx) => ({
                  ...e,
                  organization_id: s.organization_id,
                  _key: `ext:${s.organization_id}-${e.codice_fiscale || e.ospite_nome || idx}`,
                }))
              )
              .catch(() => [])
          )
        ),
      ]);

      setDocumenti(docLists.flat());
      setFatture(fatLists.flat());
      setExtraRows(extraLists.flat());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento amministrazione");
    } finally {
      setLoading(false);
    }
  }, [token, periodo]);

  useEffect(() => {
    load();
  }, [load]);

  const today = new Date().toISOString().slice(0, 10);

  const kpi = useMemo(() => {
    const docsScadenza = documenti.filter(
      (d) => d.alert_scadenza || d.stato_validita === "in_scadenza" || d.stato_validita === "scaduto"
    ).length;
    const aperte = fatture.filter((f) => FATTURE_APERTE.has(f.stato_fattura)).length;
    const scadute = fatture.filter((f) => {
      if (f.stato_fattura === "pagata" || f.stato_fattura === "archiviata") return false;
      return f.scadenza && String(f.scadenza).slice(0, 10) < today;
    }).length;
    return {
      docsScadenza,
      aperte,
      scadute,
      extra: extraRows.length,
      strutture: strutture.length,
    };
  }, [documenti, fatture, extraRows, strutture, today]);

  const scadenzeList = useMemo(() => {
    return documenti
      .filter((d) => d.scadenza && !d.mancante && d.stato_validita !== "revocato")
      .map((d) => ({ ...d, _days: daysUntil(d.scadenza) }))
      .sort((a, b) => String(a.scadenza).localeCompare(String(b.scadenza)));
  }, [documenti]);

  const fattureSummary = useMemo(() => {
    const thisMonth = monthISO();
    const emesse = fatture.filter((f) => {
      const d = (f.data_fattura || f.created_at || "").slice(0, 7);
      return d === thisMonth;
    });
    const daIncassare = fatture.filter((f) => FATTURE_APERTE.has(f.stato_fattura));
    const scadute = fatture.filter((f) => {
      if (f.stato_fattura === "pagata" || f.stato_fattura === "archiviata") return false;
      return f.scadenza && String(f.scadenza).slice(0, 10) < today;
    });
    const sum = (arr) => arr.reduce((acc, f) => acc + (Number(f.importo) || 0), 0);
    return {
      emesseCount: emesse.length,
      emesseEuro: sum(emesse),
      incassareCount: daIncassare.length,
      incassareEuro: sum(daIncassare),
      scaduteCount: scadute.length,
      scaduteEuro: sum(scadute),
    };
  }, [fatture, today]);

  const extraSummary = useMemo(() => {
    const byProduct = {};
    let totaleUnita = 0;
    for (const e of extraRows) {
      totaleUnita += e.totale_unita || 0;
      for (const r of e.righe || []) {
        const name = r.prodotto_nome || r.prodotto_codice || "Altro";
        byProduct[name] = (byProduct[name] || 0) + (r.quantita_consegnata || 0);
      }
    }
    const top = Object.entries(byProduct)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    return { top, totaleUnita, ospiti: extraRows.length };
  }, [extraRows]);

  const tableRows = useMemo(() => {
    let rows = [];
    if (tab === "documenti") {
      rows = documenti.map((d) => ({ kind: "documento", item: d, key: d._key }));
    } else if (tab === "fatture") {
      rows = fatture.map((f) => ({ kind: "fattura", item: f, key: f._key }));
    } else if (tab === "extra") {
      rows = extraRows.map((e) => ({ kind: "extra", item: e, key: e._key }));
    } else {
      rows = scadenzeList.map((d) => ({ kind: "scadenza", item: d, key: d._key }));
    }

    if (orgFilter !== "all") {
      rows = rows.filter((r) => r.item.organization_id === orgFilter);
    }
    if (tipoFilter !== "all" && (tab === "documenti" || tab === "scadenze")) {
      rows = rows.filter((r) => r.item.tipo === tipoFilter);
    }
    if (statoFilter !== "all") {
      if (tab === "documenti" || tab === "scadenze") {
        rows = rows.filter((r) => {
          if (statoFilter === "da_firmare") return r.item.stato_firma === "da_firmare";
          if (statoFilter === "alert") return r.item.alert_scadenza;
          return (r.item.stato_validita || "mancante") === statoFilter;
        });
      }
      if (tab === "fatture") {
        rows = rows.filter((r) => r.item.stato_fattura === statoFilter);
      }
    }
    const term = q.trim().toLowerCase();
    if (term) {
      rows = rows.filter((r) => {
        const it = r.item;
        const blob = [
          it.tipo_label,
          it.tipo,
          it.file_name,
          it.numero_fattura,
          it.ospite_nome,
          it.codice_fiscale,
          orgName(it.organization_id),
          ...(it.righe || []).map((x) => x.prodotto_nome),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(term);
      });
    }
    return rows;
  }, [tab, documenti, fatture, extraRows, scadenzeList, orgFilter, tipoFilter, statoFilter, q, orgName]);

  useEffect(() => {
    if (!tableRows.length) {
      setSelectedKey(null);
      return;
    }
    if (!tableRows.some((r) => r.key === selectedKey)) {
      setSelectedKey(tableRows[0].key);
    }
  }, [tableRows, selectedKey]);

  const selectedRow = tableRows.find((r) => r.key === selectedKey) || null;

  const downloadDoc = async (doc) => {
    if (!doc?.id) return;
    try {
      const res = await fetch(
        `${API_URL}/api/farmacia/documenti-essenziali/${encodeURIComponent(doc.id)}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Download fallito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || "documento.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Download fallito");
    }
  };

  const downloadFattura = async (doc) => {
    try {
      const res = await fetch(`${API_URL}/api/farmacia/fatture/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download fallito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || "fattura.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Download fallito");
    }
  };

  const exportCsv = () => {
    const headers =
      tab === "fatture"
        ? ["Struttura", "Numero", "Stato", "Importo", "Scadenza"]
        : tab === "extra"
          ? ["Struttura", "Ospite", "CF", "Unità"]
          : ["Documento", "Struttura", "Tipo", "Stato", "Scadenza"];
    const lines = [headers.join(";")];
    for (const r of tableRows) {
      const it = r.item;
      if (tab === "fatture") {
        lines.push(
          [orgName(it.organization_id), it.numero_fattura || "", it.stato_fattura || "", it.importo ?? "", it.scadenza || ""]
            .map((x) => `"${String(x).replace(/"/g, '""')}"`)
            .join(";")
        );
      } else if (tab === "extra") {
        lines.push(
          [orgName(it.organization_id), it.ospite_nome || "", it.codice_fiscale || "", it.totale_unita ?? 0]
            .map((x) => `"${String(x).replace(/"/g, '""')}"`)
            .join(";")
        );
      } else {
        const st = docDisplayStatus(it);
        lines.push(
          [it.tipo_label || it.tipo, orgName(it.organization_id), it.tipo || "", st.label, it.scadenza || ""]
            .map((x) => `"${String(x).replace(/"/g, '""')}"`)
            .join(";")
        );
      }
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `amministrazione-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div className="min-w-0 flex-1 space-y-5">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5">
          <KpiTile
            icon={AlertTriangle}
            label="Documenti in scadenza"
            value={kpi.docsScadenza}
            linkLabel="Vai a scadenze"
            tone="emerald"
            onClick={() => setTab("scadenze")}
          />
          <KpiTile
            icon={Receipt}
            label="Fatture aperte"
            value={kpi.aperte}
            linkLabel="Vai a fatture"
            tone="sky"
            onClick={() => setTab("fatture")}
          />
          <KpiTile
            icon={CalendarClock}
            label="Fatture scadute"
            value={kpi.scadute}
            linkLabel="Vai a fatture"
            tone="rose"
            onClick={() => {
              setTab("fatture");
              setStatoFilter("da_pagare");
            }}
          />
          <KpiTile
            icon={Users}
            label="Extra da riepilogare"
            value={kpi.extra}
            linkLabel="Vai a extra"
            tone="violet"
            onClick={() => setTab("extra")}
          />
          <KpiTile
            icon={Building2}
            label="Strutture attive"
            value={kpi.strutture}
            linkLabel="Vai a strutture"
            tone="amber"
            onClick={() => navigate("/farmacia/strutture")}
          />
        </div>

        <div className="flex max-w-full flex-wrap gap-1.5 rounded-xl bg-slate-100/80 p-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition sm:text-xs ${
                tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[160px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca…"
              className="h-9 rounded-xl pl-8 text-xs"
            />
          </div>
          {(tab === "documenti" || tab === "scadenze") && (
            <select
              className="h-9 rounded-xl border border-slate-200 bg-white px-2.5 text-xs"
              value={tipoFilter}
              onChange={(e) => setTipoFilter(e.target.value)}
            >
              <option value="all">Tutti i tipi</option>
              <option value="contratto">Contratto</option>
              <option value="proposta_economica">Proposta economica</option>
              <option value="nomina_trattamento_dati">Nomina / accordo dati</option>
            </select>
          )}
          <select
            className="h-9 rounded-xl border border-slate-200 bg-white px-2.5 text-xs"
            value={statoFilter}
            onChange={(e) => setStatoFilter(e.target.value)}
          >
            <option value="all">Tutti gli stati</option>
            {tab === "fatture" ? (
              Object.entries(STATO_FATTURA_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))
            ) : (
              <>
                <option value="valido">Valido</option>
                <option value="in_scadenza">In scadenza</option>
                <option value="scaduto">Scaduto</option>
                <option value="da_firmare">Da firmare</option>
                <option value="mancante">Mancante</option>
                <option value="alert">Con alert</option>
              </>
            )}
          </select>
          <select
            className="h-9 rounded-xl border border-slate-200 bg-white px-2.5 text-xs"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
          >
            <option value="all">Tutte le strutture</option>
            {strutture.map((s) => (
              <option key={s.organization_id} value={s.organization_id}>
                {s.denominazione || s.nome}
              </option>
            ))}
          </select>
          {tab === "extra" ? (
            <Input
              type="month"
              className="h-9 w-auto rounded-xl text-xs"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
            />
          ) : null}
        </div>

        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          {tableRows.length === 0 ? (
            <div className="px-4 py-16 text-center">
              <FileText className="mx-auto mb-3 h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">Nessun elemento</p>
              <p className="mt-1 text-xs text-slate-400">Nessun risultato con i filtri selezionati.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-xs">
                <thead className="border-b border-slate-100 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    {tab === "fatture" ? (
                      <>
                        <th className="px-4 py-2.5 font-semibold">Fattura</th>
                        <th className="px-3 py-2.5 font-semibold">Struttura</th>
                        <th className="px-3 py-2.5 font-semibold">Stato</th>
                        <th className="px-3 py-2.5 font-semibold">Importo</th>
                        <th className="px-3 py-2.5 font-semibold">Scadenza</th>
                        <th className="px-3 py-2.5 font-semibold">Aggiornato</th>
                      </>
                    ) : tab === "extra" ? (
                      <>
                        <th className="px-4 py-2.5 font-semibold">Ospite</th>
                        <th className="px-3 py-2.5 font-semibold">Struttura</th>
                        <th className="px-3 py-2.5 font-semibold">Unità</th>
                        <th className="px-3 py-2.5 font-semibold">Prodotti</th>
                        <th className="px-3 py-2.5 font-semibold">Periodo</th>
                      </>
                    ) : (
                      <>
                        <th className="px-4 py-2.5 font-semibold">Documento</th>
                        <th className="px-3 py-2.5 font-semibold">Struttura</th>
                        <th className="px-3 py-2.5 font-semibold">Tipo</th>
                        <th className="px-3 py-2.5 font-semibold">Stato</th>
                        <th className="px-3 py-2.5 font-semibold">Scadenza</th>
                        <th className="px-3 py-2.5 font-semibold">Ultimo aggiornamento</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tableRows.map((r) => {
                    const active = selectedKey === r.key;
                    const it = r.item;
                    if (tab === "fatture") {
                      const st = STATO_FATTURA_LABELS[it.stato_fattura] || {
                        label: it.stato_fattura,
                        className: "bg-slate-50 text-slate-600 border-slate-200",
                      };
                      return (
                        <tr
                          key={r.key}
                          className={`cursor-pointer ${active ? "bg-emerald-50/50" : "hover:bg-slate-50/80"}`}
                          onClick={() => setSelectedKey(r.key)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Receipt className="h-4 w-4 shrink-0 text-sky-600" />
                              <div>
                                <p className="font-medium text-slate-800">
                                  {it.numero_fattura || it.file_name || "Fattura"}
                                </p>
                                <p className="text-[10px] text-slate-400">{formatWhen(it.data_fattura)}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-slate-700">{orgName(it.organization_id)}</td>
                          <td className="px-3 py-3">
                            <Badge variant="outline" className={`text-[10px] ${st.className}`}>
                              {st.label}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 font-medium text-slate-800">{formatEuro(it.importo)}</td>
                          <td className="px-3 py-3 text-slate-600">{formatWhen(it.scadenza)}</td>
                          <td className="px-3 py-3 text-slate-500">{formatWhen(it.created_at)}</td>
                        </tr>
                      );
                    }
                    if (tab === "extra") {
                      return (
                        <tr
                          key={r.key}
                          className={`cursor-pointer ${active ? "bg-emerald-50/50" : "hover:bg-slate-50/80"}`}
                          onClick={() => setSelectedKey(r.key)}
                        >
                          <td className="px-4 py-3 font-medium text-slate-800">
                            {it.ospite_nome || "—"}
                          </td>
                          <td className="px-3 py-3 text-slate-700">{orgName(it.organization_id)}</td>
                          <td className="px-3 py-3 font-semibold text-slate-900">{it.totale_unita ?? 0}</td>
                          <td className="max-w-[200px] truncate px-3 py-3 text-slate-600">
                            {(it.righe || []).map((x) => x.prodotto_nome).filter(Boolean).join(", ") || "—"}
                          </td>
                          <td className="px-3 py-3 text-slate-500">{periodo}</td>
                        </tr>
                      );
                    }
                    const st = docDisplayStatus(it);
                    const days = daysUntil(it.scadenza);
                    return (
                      <tr
                        key={r.key}
                        className={`cursor-pointer ${active ? "bg-emerald-50/50" : "hover:bg-slate-50/80"}`}
                        onClick={() => setSelectedKey(r.key)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 shrink-0 text-emerald-600" />
                            <div>
                              <p className="font-medium text-slate-800">{it.tipo_label || it.tipo}</p>
                              <p className="text-[10px] text-slate-400">
                                {it.file_name || (it.mancante ? "Non caricato" : "—")}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">{orgName(it.organization_id)}</td>
                        <td className="px-3 py-3 text-slate-600">Contrattuale</td>
                        <td className="px-3 py-3">
                          <Badge variant="outline" className={`text-[10px] ${st.className}`}>
                            {st.label}
                          </Badge>
                        </td>
                        <td className="px-3 py-3 text-slate-600">
                          {formatWhen(it.scadenza)}
                          {tab === "scadenze" && days != null ? (
                            <span className="mt-0.5 block text-[10px] text-slate-400">
                              {days >= 0 ? `Tra ${days} giorni` : `${Math.abs(days)} giorni fa`}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-3 text-slate-500">
                          {formatWhen(it.updated_at || it.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {tableRows.length > 0 ? (
            <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
              Mostra {tableRows.length} elementi
            </div>
          ) : null}
        </section>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-800">Fatture farmacia</h3>
            <ul className="mt-3 space-y-2 text-xs text-slate-600">
              <li className="flex justify-between gap-2">
                <span>Emesse questo mese</span>
                <span className="font-medium text-slate-900">
                  {fattureSummary.emesseCount} · {formatEuro(fattureSummary.emesseEuro)}
                </span>
              </li>
              <li className="flex justify-between gap-2">
                <span>Da incassare</span>
                <span className="font-medium text-slate-900">
                  {fattureSummary.incassareCount} · {formatEuro(fattureSummary.incassareEuro)}
                </span>
              </li>
              <li className="flex justify-between gap-2">
                <span>Scadute</span>
                <span className="font-medium text-rose-700">
                  {fattureSummary.scaduteCount} · {formatEuro(fattureSummary.scaduteEuro)}
                </span>
              </li>
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-800">Extra per ospite</h3>
            <p className="mt-1 text-[10px] text-slate-400">Unità consegnate · {periodo}</p>
            <ul className="mt-3 space-y-2 text-xs text-slate-600">
              {extraSummary.top.length === 0 ? (
                <li className="text-slate-400">Nessun extra nel periodo</li>
              ) : (
                extraSummary.top.map(([name, qty]) => (
                  <li key={name} className="flex justify-between gap-2">
                    <span className="truncate">{name}</span>
                    <span className="font-medium text-slate-900">{qty} u.</span>
                  </li>
                ))
              )}
              <li className="flex justify-between gap-2 border-t border-slate-100 pt-2 font-semibold text-slate-900">
                <span>Totale unità / ospiti</span>
                <span>
                  {extraSummary.totaleUnita} · {extraSummary.ospiti}
                </span>
              </li>
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-800">Scadenze prossime</h3>
            <ul className="mt-3 max-h-36 space-y-2 overflow-y-auto text-xs">
              {scadenzeList.slice(0, 5).length === 0 ? (
                <li className="text-slate-400">Nessuna scadenza registrata</li>
              ) : (
                scadenzeList.slice(0, 5).map((d) => {
                  const days = daysUntil(d.scadenza);
                  return (
                    <li key={d._key} className="flex items-start justify-between gap-2">
                      <span className="min-w-0">
                        <span className="font-medium text-slate-800">{d.tipo_label}</span>
                        <span className="mt-0.5 block truncate text-[10px] text-slate-400">
                          {orgName(d.organization_id)}
                        </span>
                      </span>
                      <span className="shrink-0 text-[10px] text-slate-500">
                        {days != null && days >= 0 ? `Tra ${days} giorni` : formatWhen(d.scadenza)}
                      </span>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            className="rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]"
            onClick={() => {
              const first = strutture[0]?.organization_id;
              if (first) navigate(`/farmacia/struttura/${encodeURIComponent(first)}`);
              else toast.message("Nessuna struttura collegata");
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Nuovo documento
          </Button>
          <Button asChild variant="outline" className="rounded-xl">
            <Link to="/farmacia/fatturazione">
              <Receipt className="mr-1.5 h-4 w-4" />
              Registra fattura
            </Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-xl border-violet-200 text-violet-800 hover:bg-violet-50"
            onClick={() => {
              setTab("extra");
              toast.message("Gli extra derivano dagli ordini consegnati nel mese selezionato.");
            }}
          >
            <Users className="mr-1.5 h-4 w-4" />
            Extra ospite
          </Button>
          <Button type="button" variant="outline" className="rounded-xl" onClick={exportCsv}>
            <Download className="mr-1.5 h-4 w-4" />
            Esporta riepilogo
          </Button>
        </div>

        {selectedRow ? (
          <div className="xl:hidden">
            <DetailPanel
              kind={selectedRow.kind}
              item={selectedRow.item}
              strutturaNome={orgName(selectedRow.item.organization_id)}
              onClose={() => setSelectedKey(null)}
              onDownloadDoc={downloadDoc}
              onDownloadFattura={downloadFattura}
            />
          </div>
        ) : null}
      </div>

      <div className="hidden xl:block">
        <DetailPanel
          kind={selectedRow?.kind}
          item={selectedRow?.item}
          strutturaNome={selectedRow ? orgName(selectedRow.item.organization_id) : ""}
          onClose={() => setSelectedKey(null)}
          onDownloadDoc={downloadDoc}
          onDownloadFattura={downloadFattura}
        />
      </div>
    </div>
  );
}
